<script lang="ts">
import type { DefaultViewOptions } from '#layers/blocks';
import type { CompositePrimaryKey, GlobalCollectionId } from '#layers/data-model';

export interface CollectionListingDrawerProps {
	collectionId: GlobalCollectionId;
	title?: string;
	selection?: BlockRootContext['selection'];
	selectionMode?: BlockRootContext['selectionMode'];
	filter?: BlockRootContext['filter'];
	defaultView?: DefaultViewOptions;
}
</script>

<script lang="ts" setup>
import type { BlockRootContext } from '#layers/block-engine';

import { useEmitAsProps } from 'reka-ui';
import { computed, shallowRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTranslationUtils } from '#layers/i18n';

const props = defineProps<CollectionListingDrawerProps>();

const emit = defineEmits<{
	close: [payload?: { selection: CompositePrimaryKey[] }];
	open: [key: CompositePrimaryKey];
}>();

const emitAsProps = useEmitAsProps(emit);

const { t } = useI18n();
const { translateString } = useTranslationUtils();

const selectionTitle = computed(() => {
	switch (props.selectionMode) {
		case 'single':
			return t('common.select_item');
		case 'multiple':
			return t('common.select_items');
		default:
			// eslint-disable-next-line unicorn/no-useless-undefined
			return undefined;
	}
});

const title = computed(() => props.title ? translateString(props.title) : selectionTitle.value);
const tempSelection = shallowRef(props.selection);

// Use a computed to prevent the block context object from being re-created on every render
// Which would trigger the watcher on `filter` in `useItems` unnecessarily
const blockContext = computed<BlockRootContext>(() => ({
	selection: tempSelection.value,
	selectionMode: props.selectionMode,
	filter: props.filter,
	pageType: 'listing',
	pageIsFullWidth: true,
	pageDisplayMode: 'drawer',
	defaultView: props.defaultView,
}));
</script>

<template>
	<DrawerPage
		:title
		:attach-to-route="false"
		:footer-actions="selectionMode ? [{
			label: $t('common.confirm'),
			onClick: () =>
				emit('close', { selection: tempSelection ?? [] }),
		}] : []"
		:ui="{
			container: 'gap-0',
			body: 'p-0',
		}"
		@close="() => emit('close')"
	>
		<CollectionDefaultBlockRender
			class="h-full"
			type="listing"
			:collection-id
			:block-context
			v-bind="emitAsProps"
			@update:selection="tempSelection = $event"
		/>
	</DrawerPage>
</template>
